import { useEffect, useMemo, useState } from "react";
import {
  StaffAppointmentsViewModel,
  StaffFinanceViewModel,
  StaffInventoryViewModel,
} from "@lumora/core";
import { client, SITE } from "../client.js";
import { staffSession } from "../lib/vms.js";
import StaffAppointmentsView from "../views/staff/StaffAppointmentsView.js";
import StaffFinanceView from "../views/staff/StaffFinanceView.js";
import StaffInventoryView from "../views/staff/StaffInventoryView.js";

type StaffTab = "appointments" | "inventory" | "finance";

export function StaffDashboard() {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [tab, setTab] = useState<StaffTab>("appointments");

  const appointmentsVm = useMemo(() => new StaffAppointmentsViewModel(client), []);
  const inventoryVm = useMemo(() => new StaffInventoryViewModel(client, SITE), []);
  const financeVm = useMemo(() => new StaffFinanceViewModel(client), []);

  useEffect(() => {
    setSignedIn(staffSession.check() !== null);
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    void appointmentsVm.load();
    void inventoryVm.load();
    void financeVm.load();
  }, [signedIn, appointmentsVm, inventoryVm, financeVm]);

  const role = client.claims()?.role ?? "—";

  if (signedIn === null) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center text-sm text-gray-500">Checking sign-in…</div>
    );
  }

  if (!signedIn) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="mb-4 text-2xl font-bold">Staff dashboard</h1>
        <p className="mb-6 text-gray-600">Sign in from the public site staff portal first.</p>
        <a href="#" className="text-red-600 font-semibold underline" onClick={() => { window.location.hash = ""; }}>
          Back to site
        </a>
      </div>
    );
  }

  const tabDefs: { id: StaffTab; label: string; show: boolean }[] = [
    { id: "appointments", label: "Appointments", show: appointmentsVm.canAccess() },
    { id: "inventory", label: "Inventory", show: inventoryVm.canView() },
    { id: "finance", label: "Finance", show: financeVm.canReadBusiness() || financeVm.canManageInvestors() },
  ];
  const tabs = tabDefs.filter((t) => t.show);

  const activeTab = tabs.some((t) => t.id === tab) ? tab : (tabs[0]?.id ?? "appointments");

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white px-4 py-4">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight">Staff dashboard</h1>
            <p className="text-xs text-gray-500">
              Signed in · role {role} · tenant {client.claims()?.tenantId ?? "—"}
            </p>
          </div>
          <a
            href="#"
            className="text-xs font-bold uppercase text-red-600"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = "";
            }}
          >
            Exit to site
          </a>
        </div>
        <nav className="mx-auto mt-4 flex max-w-4xl gap-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-full px-4 py-1 text-xs font-bold uppercase ${
                activeTab === t.id ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-4xl px-4 py-8">
        {tabs.length === 0 && (
          <p className="text-sm text-gray-500">No staff modules available for your role.</p>
        )}
        {activeTab === "appointments" && appointmentsVm.canAccess() && (
          <StaffAppointmentsView vm={appointmentsVm} />
        )}
        {activeTab === "inventory" && inventoryVm.canView() && <StaffInventoryView vm={inventoryVm} />}
        {activeTab === "finance" && (financeVm.canReadBusiness() || financeVm.canManageInvestors()) && (
          <StaffFinanceView vm={financeVm} />
        )}
      </main>
    </div>
  );
}
